import { makeStyles } from '@fluentui/react-components';
import { useCallback } from 'react';

import { type DayCell, pendingNetHours } from '../calendar';
import { ModalBackdrop, SectionLabel } from '../components';
import { useCurrentUser } from '../current-user';
import { palette, tokens } from '../theme';
import { usePending } from '../worklog';
import { DialogFooter, DialogHeader, HoursPanel, QuickAddSection, TodaysEntriesSection, useQuickAdd } from './parts';

export type DayDialogProps = {
    cell: DayCell;
    onClose: () => void;
};

export const DayDialog: React.FC<DayDialogProps> = ({ cell, onClose }) => {
    const classes = useStyles();
    const { forDate, clearForDate } = usePending();
    const { toggleDayOff, canEditDaysOff } = useCurrentUser();
    const pendingForDay = forDate(cell.date);

    const isReadOnly = cell.nonWorking != null;
    const pendingNet = pendingNetHours(pendingForDay);

    const {
        selected,
        hoursPanelTarget,
        setHoursInput,
        currentHours,
        remaining,
        canAdd,
        handleSelectWorkItem,
        handleCloseEntry,
        handleAdd,
        reset: resetQuickAdd,
    } = useQuickAdd(cell.date, cell.savedHours);

    const handleToggleDayOff = useCallback(() => {
        if (!canEditDaysOff) {
            return;
        }
        void toggleDayOff(cell.date);
        onClose();
    }, [toggleDayOff, canEditDaysOff, cell.date, onClose]);

    const handleReset = useCallback(() => {
        clearForDate(cell.date);
        resetQuickAdd();
    }, [clearForDate, cell.date, resetQuickAdd]);

    const showHoursPanel = selected !== null && !isReadOnly;

    return (
        <ModalBackdrop onClose={onClose} ariaLabel="Day entries">
            <div className={classes.panel}>
                <div className={classes.inner}>
                    <DialogHeader
                        cell={cell}
                        logged={cell.savedHours}
                        pending={pendingNet}
                        onToggleDayOff={canEditDaysOff ? handleToggleDayOff : undefined}
                        onClose={onClose}
                    />

                    <div className={classes.divider} />

                    <div className={classes.body}>
                        <div className={classes.leftColumn}>
                            {isReadOnly ? (
                                <>
                                    <SectionLabel text="Quick add" />
                                    <p className={classes.readOnlyHint}>Non-working day — adding entries is disabled.</p>
                                </>
                            ) : (
                                <QuickAddSection date={cell.date} selected={selected} onSelect={handleSelectWorkItem} />
                            )}
                        </div>
                        <div className={classes.rightColumn}>
                            <SectionLabel text="Time entries" />
                            <div className={classes.entriesScroll}>
                                <TodaysEntriesSection cell={cell} />
                            </div>
                            {showHoursPanel && hoursPanelTarget && (
                                <HoursPanel
                                    item={hoursPanelTarget}
                                    currentHours={currentHours}
                                    remaining={remaining}
                                    canAdd={canAdd}
                                    onHoursChange={setHoursInput}
                                    onAdd={handleAdd}
                                    onClose={handleCloseEntry}
                                />
                            )}
                        </div>
                    </div>

                    <div className={classes.divider} />

                    <DialogFooter pendingCount={pendingForDay.length} onReset={handleReset} onSave={onClose} />
                </div>
            </div>
        </ModalBackdrop>
    );
};

const useStyles = makeStyles({
    panel: {
        backgroundColor: palette.panelBg,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: palette.dialogShadow,
        width: 'min(96vw, 1400px)',
        height: 'min(94vh, 900px)',
        overflow: 'hidden',
    },
    inner: {
        display: 'grid',
        gridTemplateRows: 'auto auto minmax(0, 1fr) auto auto',
        height: '100%',
        padding: tokens.spacingHorizontalXL,
        gap: tokens.spacingVerticalM,
        boxSizing: 'border-box',
    },
    divider: {
        height: '1px',
        backgroundColor: palette.cardBorder,
    },
    body: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: tokens.spacingHorizontalXL,
        minHeight: 0,
        overflow: 'hidden',
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    rightColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        gap: tokens.spacingVerticalM,
    },
    entriesScroll: {
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: tokens.spacingHorizontalXS,
    },
    readOnlyHint: {
        margin: 0,
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
    },
});
